import React, { useState, useEffect, useContext } from 'react';
import api from '../../config/axiosConfig';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { AddCircleOutline } from '@mui/icons-material';
import UserGroupContext from '../../contexts/UserGroupContext';
import styles from './css/YearlytargetTab.module.css';

function YearlytargetTab({ country }) {
  const userGroups = useContext(UserGroupContext);

  const [targetYear, setTargetYear] = useState(new Date().getFullYear());
  const [yearlyTargetData, setYearlyTargetData] = useState({});
  const [currentYearData, setCurrentYearData] = useState([]);
  const [newTargetData, setNewTargetData] = useState({});
  const [updateTargetData, setUpdateTargetData] = useState({});
  const [originalTargetData, setOriginalTargetData] = useState({});
  const [addTarget, setAddTarget] = useState(false);
  const [updateTarget, setUpdateTarget] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmittingAdd, setIsSubmittingAdd] = useState(false);
  const [isSubmittingUpdate, setIsSubmittingUpdate] = useState(false);

  // 可選年份
  const targetYears = Array.from(
    { length: new Date().getFullYear() - 2022 + 1 },
    (_, i) => 2023 + i
  );

  // 後端對應的欄位
  const monthKeys = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december'
  ];

  // 讀取年度目標 & 讀取當年度實際數據
  const fetchData = async () => {
    setLoading(true);
    try {
      // 若 country 為 null，直接 return，不抓資料
      if (!country) return;

      const lineApiPrefix = country === 'KR' ? '/korea/api' : '/japan/api';

      // 1) 取得年度目標
      const targetRes = await api.get(`${lineApiPrefix}/YearlyTarget/?year=${targetYear}`);
      // 2) 取得該年度每月實際外幣報價
      const currentYearRes = await api.get(
        `${lineApiPrefix}/monthly-foreign-currency/?year=${targetYear}`
      );

      setYearlyTargetData(targetRes.data);
      setCurrentYearData(currentYearRes.data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('資料取得錯誤，請稍後再試。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, targetYear]);

  // 新增年度目標
  const handleSubmitNewTarget = async () => {
    setIsSubmittingAdd(true);
    try {
      if (!country) return; // 安全檢查

      const lineApiPrefix = country === 'KR' ? '/korea/api' : '/japan/api';
      newTargetData.year = targetYear;

      await api.post(`${lineApiPrefix}/YearlyTarget/`, newTargetData);
      setAddTarget(false);
      setNewTargetData({});
      // 重新抓取
      fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmittingAdd(false);
    }
  };

  // 更新年度目標
  const handleSubmitUpdateTarget = async () => {
    setIsSubmittingUpdate(true);
    try {
      const modifiedFields = {};
      for (const key in updateTargetData) {
        // 只更新有改動過的
        if (updateTargetData[key] !== originalTargetData[key]) {
          modifiedFields[key] = monthKeys.includes(key)
            ? parseFloat(updateTargetData[key])
            : updateTargetData[key];
        }
      }
      if (Object.keys(modifiedFields).length > 0 && country) {
        const lineApiPrefix = country === 'KR' ? '/korea/api' : '/japan/api';
        await api.patch(
          `${lineApiPrefix}/YearlyTarget/${updateTargetData.id}/`,
          modifiedFields
        );
      }
      setUpdateTarget(false);
      setUpdateTargetData({});
      setOriginalTargetData({});
      // 重新抓取
      fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmittingUpdate(false);
    }
  };

  // 計算整年達成率
  let overallTarget = 0;
  let overallActual = 0;
  if (yearlyTargetData?.success && yearlyTargetData.data && currentYearData?.data) {
    const targetObj = yearlyTargetData.data[0];
    const actualObj = currentYearData.data[0];
    monthKeys.forEach((key) => {
      overallTarget += parseFloat(targetObj[key]) || 0;
      overallActual += parseFloat(actualObj[key]) || 0;
    });
  }
  const achievementRate = overallTarget === 0 ? 0 : (overallActual / overallTarget) * 100;

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        <Box className={styles['dataCard-box']} style={{ marginTop: '20px' }}>
          <Box className={styles['chartCard-title-box']}>
            <Typography
              sx={{ position: 'absolute', left: '0', fontSize: '20px', fontWeight: 'bold', color: '#1a224c' }}
            >
              年度目標統計
            </Typography>

            {userGroups.includes('管理員') && yearlyTargetData?.success && (
              <Button
                variant="outlined"
                size="small"
                sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}
                onClick={() => {
                  setUpdateTarget(true);
                  setUpdateTargetData(yearlyTargetData.data[0]);
                  setOriginalTargetData(yearlyTargetData.data[0]);
                }}
              >
                修改目標
              </Button>
            )}

            <Box sx={{ position: 'absolute', right: '0' }}>
              <Autocomplete
                options={targetYears}
                getOptionLabel={(option) => String(option)}
                disableClearable
                value={targetYear}
                sx={{ width: '75px' }}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="年份選擇" variant="standard" />
                )}
                onChange={(event, newValue) => {
                  setTargetYear(newValue || new Date().getFullYear());
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: '10px' }}>
            {yearlyTargetData?.success ? (
              <>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <BarChart
                    xAxis={[
                      {
                        data: [
                          '1月','2月','3月','4月','5月','6月',
                          '7月','8月','9月','10月','11月','12月'
                        ],
                        scaleType: 'band'
                      }
                    ]}
                    series={[
                      {
                        data: Object.entries(currentYearData.data[0] || {})
                          .filter(([key]) => monthKeys.includes(key))
                          .map(([, value]) => parseFloat(value)),
                        label: '實際',
                        // 顯示「實際值(達成%)」的格式
                        valueFormatter: (value, index) => {
                          const targetKey = monthKeys[index.dataIndex];
                          const safeParse = (val) =>
                            val == null || val === '' ? 0 : parseFloat(String(val).replace(/,/g, '')) || 0;
                          const formatNumber = (num) => num.toLocaleString();
                          const actualVal = safeParse(value);
                          const targetVal = safeParse(yearlyTargetData.data[0][targetKey]);
                          const pct = targetVal > 0 ? ((actualVal / targetVal) * 100).toFixed(2) : 'N/A';
                          return `${formatNumber(actualVal)} (${pct}%)`;
                        }
                      },
                      {
                        data: Object.entries(yearlyTargetData.data[0] || {})
                          .filter(([key]) => monthKeys.includes(key))
                          .map(([, value]) => parseFloat(value)),
                        label: '目標'
                      }
                    ]}
                    width={800}
                    height={300}
                  />
                </Box>

                <Box className={styles['achievement-container']}>
                  <Box className={styles['progress-wrapper']}>
                    <LinearProgress
                      variant="determinate"
                      value={achievementRate}
                      className={styles['progress-bar']}
                    />
                    <Typography variant="caption" className={styles['progress-overlay']}>
                      {overallActual.toLocaleString()}/{overallTarget.toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography variant="body2" className={styles['achievement-percent']}>
                    {achievementRate.toFixed(2)}%
                  </Typography>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  p: 3,
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  textAlign: 'center',
                  backgroundColor: '#f9f9f9',
                  width: '100%',
                  maxWidth: 400
                }}
              >
                <Typography variant="body1" color="textSecondary">
                  {userGroups.includes('管理員')
                    ? '尚未設定目標，請點擊下方按鈕新增目標'
                    : '目前尚未設定目標，請聯絡管理員設定'}
                </Typography>
                {userGroups.includes('管理員') && (
                  <Button
                    variant="contained"
                    disableRipple
                    onClick={() => setAddTarget(true)}
                    startIcon={<AddCircleOutline />}
                    sx={{
                      mt: 2,
                      textTransform: 'none',
                      backgroundColor: '#1976d2',
                      '&:hover': { backgroundColor: '#115293' }
                    }}
                  >
                    新增目標
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* 僅管理員可新增/修改 */}
      {userGroups.includes('管理員') && (
        <>
          {/* 新增目標 */}
          <Dialog open={addTarget} onClose={() => setAddTarget(false)}>
            <DialogTitle className={styles.dialogTitle}>新增 {targetYear} 的每月份目標</DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <Box className={styles.addingTargetBox}>
                {monthKeys.map((month, index) => (
                  <TextField
                    key={month}
                    label={`${index + 1}月`}
                    type="number"
                    variant="standard"
                    fullWidth
                    margin="normal"
                    onChange={(event) =>
                      setNewTargetData({ ...newTargetData, [month]: event.target.value })
                    }
                  />
                ))}
              </Box>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button
                className={`${styles['custom-button']} ${styles['cancel-button']}`}
                onClick={() => setAddTarget(false)}
              >
                取消
              </Button>
              <Button
                className={`${styles['custom-button']} ${styles['save-button']}`}
                onClick={handleSubmitNewTarget}
                disabled={isSubmittingAdd}
              >
                儲存
              </Button>
            </DialogActions>
          </Dialog>

          {/* 更新目標 */}
          <Dialog open={updateTarget} onClose={() => setUpdateTarget(false)}>
            <DialogTitle className={styles.dialogTitle}>更新 {targetYear} 的每月份目標</DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <Box className={styles.addingTargetBox}>
                {monthKeys.map((month, index) => (
                  <TextField
                    key={month}
                    label={`${index + 1}月`}
                    type="number"
                    variant="standard"
                    fullWidth
                    margin="normal"
                    value={updateTargetData[month] || ''}
                    onChange={(event) =>
                      setUpdateTargetData({ ...updateTargetData, [month]: event.target.value })
                    }
                  />
                ))}
              </Box>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button
                className={`${styles['custom-button']} ${styles['cancel-button']}`}
                onClick={() => setUpdateTarget(false)}
              >
                取消
              </Button>
              <Button
                className={`${styles['custom-button']} ${styles['save-button']}`}
                onClick={handleSubmitUpdateTarget}
                disabled={isSubmittingUpdate}
              >
                儲存
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}

export default YearlytargetTab;
